const tracked = new Map();
let currentRoot = null;
let nextScopeId = 0;
const scopeIds = new WeakMap();
const controls = 'input:not([type=hidden]):not([type=file]):not([type=button]):not([type=submit]):not([type=password]),select,textarea,[contenteditable=true],.sliding-choice';
const ignored = '[data-unsaved-ignore],nav,.sidebar,.site-nav,.rubrica-search,.rubrica-filters,.agenda-toolbar,.agenda-filters,.documents-browser-tools,.documents-area-filters,.summary-layout-toolbar,.summary-widget-controls,.lucky-assistant-panel,.accesso-page';

const readValue = node => {
  if (node.matches('.sliding-choice')) return [...node.querySelectorAll('button')].map(button => button.getAttribute('aria-pressed')).join('|');
  if (node.matches('[contenteditable=true]')) return node.innerHTML;
  if (['checkbox','radio'].includes(node.type)) return String(node.checked);
  const value = node.value ?? '';
  if (node.type === 'number' || node.inputMode === 'decimal') {
    if (!String(value).trim()) return '';
    const number = Number(String(value).replace(/\s/g,'').replace(/\.(?=\d{3}(?:\D|$))/g,'').replace(',','.'));
    if (Number.isFinite(number)) return String(number);
  }
  return String(value);
};
const scopeFor = node => node.closest('form,[role=dialog],[data-unsaved-scope],.repairer-modal,.draft-modal,.practice-modal');
const keyFor = (node, scope) => {
  let scopeKey = scope.getAttribute('data-unsaved-scope');
  if (!scopeKey) { if (!scopeIds.has(scope)) scopeIds.set(scope, `editor-${++nextScopeId}`); scopeKey = scopeIds.get(scope); }
  // A structural path avoids collisions among repeated name/id attributes.
  let item = node; const path = [];
  while (item && item !== scope) { path.unshift([...item.parentElement.children].indexOf(item)); item = item.parentElement; }
  return `${scopeKey}/${node.id || node.name || node.getAttribute('aria-label') || node.tagName}:${path.join('.')}`;
};
const visualFor = node => node.matches('.sliding-choice') ? node : ['checkbox','radio'].includes(node.type)
  ? node.closest('.cisa-switch,.compact-switch,.note-important,label') || node : node;
const paint = record => {
  if (!record.node.isConnected) return;
  const dirty = record.edited && readValue(record.node) !== record.original;
  const visual = visualFor(record.node);
  if (record.visual && record.visual !== visual) record.visual.classList.remove('unsaved-control');
  record.visual = visual;
  visual.classList.toggle('unsaved-control', dirty);
};
const eligible = node => node && node.matches(controls) && !node.disabled && !node.readOnly && node.type !== 'search' && !node.closest(ignored) && scopeFor(node);

export function installUnsavedFeedback(root) {
  currentRoot = root;
  const register = (node, refreshOriginal = false) => {
    if (!eligible(node)) return null;
    const scope = scopeFor(node), key = keyFor(node, scope);
    let record = tracked.get(key);
    if (!record) { record = { key, node, scope, original: readValue(node), edited: false }; tracked.set(key, record); }
    else { record.node = node; record.scope = scope; if (!record.edited && refreshOriginal) record.original = readValue(node); }
    paint(record); return record;
  };
  const scan = () => { for (const [key,record] of tracked) if (!record.node.isConnected && key.startsWith('editor-')) tracked.delete(key); root.querySelectorAll(controls).forEach(node => register(node, true)); };
  const target = event => event.target.closest?.('.sliding-choice') || event.target.closest?.(controls);
  const prepare = event => { const node = target(event); if (node) register(node, true); };
  let frame = 0;
  const refresh = () => {
    if (frame) return;
    frame = requestAnimationFrame(() => { frame = 0; scan(); for (const record of tracked.values()) if (record.edited) paint(record); });
  };
  const edited = event => {
    const node = target(event), record = node && register(node);
    if (record) { record.edited = true; refresh(); }
  };
  const clicked = event => { if (event.target.closest?.('.sliding-choice button')) edited(event); };
  scan();
  root.addEventListener('focusin', prepare, true);
  root.addEventListener('pointerdown', prepare, true);
  root.addEventListener('beforeinput', prepare, true);
  root.addEventListener('input', edited, true);
  root.addEventListener('change', edited, true);
  root.addEventListener('click', clicked, true);
  const observer = new MutationObserver(refresh);
  observer.observe(root.body || root, { subtree:true, childList:true, attributes:true, attributeFilter:['aria-pressed','checked','value'] });
  return () => {
    observer.disconnect(); cancelAnimationFrame(frame);
    for (const [name, handler] of [['focusin',prepare],['pointerdown',prepare],['beforeinput',prepare],['input',edited],['change',edited],['click',clicked]]) root.removeEventListener(name, handler, true);
    for (const record of tracked.values()) record.visual?.classList.remove('unsaved-control');
    tracked.clear(); currentRoot = null;
  };
}

// Capture at request start; commit ONLY after the corresponding operation succeeds.
// Later edits stay amber even if an earlier request finishes in the meantime.
export function captureUnsavedFeedback(scope) {
  const snapshots = [...tracked.values()].filter(record => record.edited && (Array.isArray(scope) ? scope.some(prefix => record.key.startsWith(prefix)) : typeof scope === 'string'
    ? record.key.startsWith(scope) : scope && (scope === record.scope || scope.contains(record.node))))
    .map(record => ({ record, value: readValue(record.node) }));
  return (notify = true) => {
    for (const { record, value } of snapshots) { record.original = value; record.edited = readValue(record.node) !== value; paint(record); }
    if (notify && currentRoot) window.dispatchEvent(new Event('cisa:saved-feedback'));
  };
}
